/**
 * Centralized Query Keys for TanStack Query
 * 
 * STRICT RULE: No hardcoded strings in hooks. All keys must be defined here.
 * This enables type-safe cache invalidation and prevents key collisions.
 */

export const queryKeys = {
  // Authentication
  auth: {
    all: ['auth'],
    user: () => ['auth', 'user'],
    permissions: () => ['auth', 'permissions'],
  },

  // Users/Employees
  users: {
    all: ['users'],
    list: (params) => ['users', 'list', params],
    detail: (id) => ['users', 'detail', id],
  },

  // Teams
  teams: {
    all: ['teams'],
    list: (params) => ['teams', 'list', params],
    detail: (id) => ['teams', 'detail', id],
    members: (teamId) => ['teams', teamId, 'members'],
  },

  // Invitations
  invitations: {
    all: ['invitations'],
    list: (params) => ['invitations', 'list', params],
    detail: (id) => ['invitations', 'detail', id],
  },

  // Roles
  roles: {
    all: ['roles'],
    list: (params) => ['roles', 'list', params],
    detail: (id) => ['roles', 'detail', id],
  },

  // Permissions
  permissions: {
    all: ['permissions'],
    list: (params) => ['permissions', 'list', params],
  },

  // Projects
  projects: {
    all: ['projects'],
    list: (params) => ['projects', 'list', params],
    detail: (id) => ['projects', 'detail', id],
  },

  // Project Members
  projectMembers: {
    all: ['projectMembers'],
    list: (projectId) => ['projectMembers', projectId, 'list'],
    status: (projectId) => ['project-member-status', projectId],
  },

  // Master Projects
  masterProjects: {
    all: ['masterProjects'],
    list: (params) => ['masterProjects', 'list', params],
    detail: (id) => ['masterProjects', 'detail', id],
  },

  // Master Project Schedule
  masterProjectSchedule: {
    all: ['masterProjectSchedule'],
    list: (params) => ['masterProjectSchedule', 'list', params],
  },

  // Product Development Projects
  productDevelopmentProjects: {
    all: ['productDevelopmentProjects'],
    list: (params) => ['productDevelopmentProjects', 'list', params],
    detail: (id) => ['productDevelopmentProjects', 'detail', id],
  },

  // Future project views - uncomment as needed:
  applicationLabProjects: {
    all: ['applicationLabProjects'],
    list: (params) => ['applicationLabProjects', 'list', params],
    detail: (id) => ['applicationLabProjects', 'detail', id],
  },
  sensoryLabProjects: {
    all: ['sensoryLabProjects'],
    list: (params) => ['sensoryLabProjects', 'list', params],
    detail: (id) => ['sensoryLabProjects', 'detail', id],
  },
  businessDevelopmentProjects: {
    all: ['businessDevelopmentProjects'],
    list: (params) => ['businessDevelopmentProjects', 'list', params],
    detail: (id) => ['businessDevelopmentProjects', 'detail', id],
  },

  // Project History
  projectHistory: {
    all: ['projectHistory'],
    list: (projectId, params) => ['projectHistory', 'list', projectId, params],
  },

  // Project Comments
  comments: {
    all: ['comments'],
    list: (projectId, params) => ['comments', 'list', projectId, params],
    detail: (id) => ['comments', 'detail', id],
  },

  // Project Tasks
  projectTasks: {
    all: ['projectTasks'],
    list: (params) => ['projectTasks', 'list', params],
    detail: (id) => ['projectTasks', 'detail', id],
    byProject: (projectId, params) => ['projectTasks', 'byProject', projectId, params],
  },

  // Internal Tasks
  internalTasks: {
    all: ['internalTasks'],
    list: (params) => ['internalTasks', 'list', params],
    detail: (id) => ['internalTasks', 'detail', id],
    byUser: (userId, params) => ['internalTasks', 'byUser', userId, params],
    byTeam: (teamId, params) => ['internalTasks', 'byTeam', teamId, params],
  },

  // Recipes
  recipes: {
    all: ['recipes'],
    list: (params) => ['recipes', 'list', params],
    detail: (id) => ['recipes', 'detail', id],
    byProject: (projectId, params) => ['recipes', 'byProject', projectId, params],
    byCategory: (categoryId, params) => ['recipes', 'byCategory', categoryId, params],
  },

  // Categories
  categories: {
    all: ['categories'],
    list: (params) => ['categories', 'list', params],
    detail: (id) => ['categories', 'detail', id],
  },

  // Subcategories
  subcategories: {
    all: ['subcategories'],
    list: (params) => ['subcategories', 'list', params],
    detail: (id) => ['subcategories', 'detail', id],
    byCategory: (categoryId, params) => ['subcategories', 'byCategory', categoryId, params],
  },

  // Sub-subcategories
  subsubcategories: {
    all: ['subsubcategories'],
    list: (params) => ['subsubcategories', 'list', params],
    detail: (id) => ['subsubcategories', 'detail', id],
    bySubcategory: (subcategoryId, params) => ['subsubcategories', 'bySubcategory', subcategoryId, params],
  },

  // Application Tags
  applicationTags: {
    all: ['applicationTags'],
    list: (params) => ['applicationTags', 'list', params],
    detail: (id) => ['applicationTags', 'detail', id],
    bySubcategory: (subcategoryId, params) => ['applicationTags', 'bySubcategory', subcategoryId, params],
  },

  // BFF Product Codes
  bffProductCodes: {
    all: ['bffProductCodes'],
    list: (params) => ['bffProductCodes', 'list', params],
    detail: (id) => ['bffProductCodes', 'detail', id],
    bySegment: (segment, params) => ['bffProductCodes', 'bySegment', segment, params],
  },

  // Access History
  accessHistory: {
    all: ['accessHistory'],
    list: (params) => ['accessHistory', 'list', params],
  },

  // Modules
  modules: {
    all: ['modules'],
    list: () => ['modules', 'list'],
    submodules: (moduleKey) => ['modules', moduleKey, 'submodules'],
  },
};
